import { WarningTwoIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import {
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import { Icon } from 'packages/common-frontend/components/icons';
import {
  formatSelectOptions,
  Select,
} from 'packages/common-frontend/components/Select';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';
import { useGetOrganizationLoanProductsQuery } from 'packages/organization/requests/organization.gql.generated';
import { FiSave } from 'react-icons/fi';

// Types
type FileInsertionConfig = CreditAgreement.Config.Reporting.Outputs.Xlsx.FileInsertionConfig;
type FileInsertionType = CreditAgreement.Config.Reporting.Outputs.Xlsx.FileInsertionType;

// Constants
const fileInsertTypes =
  CreditAgreement.Config.Reporting.Outputs.Xlsx.FileInsertionTypeIterable;
const labelWidth = '60';
const inputWidth = '80';

// Exports
type ReportFileInsertionConfigContainerProps = {
  organizationId: string;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  sheetIdxConfigPropertyKey: string;
  targetSheetConfig: FileInsertionConfig;
  bankAccounts: CreditAgreement.DomainEntity.Items.BankAccountEntity[];
  fileInsertAthenaQueries: CreditAgreement.Config.Reporting.Inputs.FileInsertAthenaQuery[];
};

const createFileInsertionConfigDefault = ({
  loanProductId,
  bankAccountId,
  fileType,
  reportingDateFormula,
  artifactSlug,
  startingExcelCoordinate,
}: {
  loanProductId: string | undefined;
  bankAccountId: string | undefined;
  fileType: CreditAgreement.Config.Reporting.Outputs.Xlsx.FileInsertionConfigDefault['fileType'];
  reportingDateFormula: string;
  artifactSlug: string | undefined;
  startingExcelCoordinate: string;
}): CreditAgreement.Config.Reporting.Outputs.Xlsx.FileInsertionConfigDefault => ({
  loanProductId,
  bankAccountId,
  fileType,
  reportingDateFormula,
  artifactSlug,
  startingExcelCoordinate,
});

const createFileInsertionConfigStatementDailySummary = ({
  fileType,
  reportingDateFormula,
  startingExcelCoordinate,
  requiresLatestStatementForPeriod,
  statementStartDateFormula,
  statementEndDateFormula,
}: {
  fileType: 'statementDailySummary';
  reportingDateFormula: string;
  startingExcelCoordinate: string;
  requiresLatestStatementForPeriod: boolean;
  statementStartDateFormula: string;
  statementEndDateFormula: string;
}): CreditAgreement.Config.Reporting.Outputs.Xlsx.FileInsertionConfigStatementDailySummary => ({
  fileType,
  reportingDateFormula,
  startingExcelCoordinate,
  requiresLatestStatementForPeriod,
  statementStartDateFormula,
  statementEndDateFormula,
});

const createFileInsertionConfigAthenaQuery = ({
  reportingDateFormula,
  startingExcelCoordinate,
  athenaQueryName,
}: {
  reportingDateFormula: string;
  startingExcelCoordinate: string;
  athenaQueryName: string;
}): CreditAgreement.Config.Reporting.Outputs.Xlsx.AthenaQueryFileInsertionConfig => ({
  fileType: 'athenaQuery',
  reportingDateFormula,
  startingExcelCoordinate,
  athenaQueryName,
});

const createFileInsertionConfig = ({
  loanProductId,
  bankAccountId,
  fileType,
  reportingDateFormula,
  artifactSlug,
  startingExcelCoordinate,
  statementStartDateFormula,
  statementEndDateFormula,
  athenaQueryName,
}: {
  loanProductId: string | undefined;
  bankAccountId: string | undefined;
  fileType: FileInsertionType;
  reportingDateFormula: string;
  artifactSlug: string | undefined;
  startingExcelCoordinate: string;
  statementStartDateFormula: string;
  statementEndDateFormula: string;
  athenaQueryName: string;
}): FileInsertionConfig => {
  switch (fileType) {
    case 'statementDailySummary':
      return createFileInsertionConfigStatementDailySummary({
        fileType,
        reportingDateFormula,
        startingExcelCoordinate,
        requiresLatestStatementForPeriod: true,
        statementStartDateFormula,
        statementEndDateFormula,
      });
    case 'rawMasterLoanTape':
    case 'rawAssignedLoanTape':
    case 'rawCovenantLoanTape':
    case 'rawRepaymentTape':
    case 'rawSupplementalTape':
    case 'generatedMasterLoanTape':
    case 'generatedAssignedLoanTape':
    case 'generatedCovenantLoanTape':
    case 'generatedRepaymentTape':
    case 'generatedSupplementalTape':
    case 'intermediateTape':
    case 'extendedResponseTape':
    case 'newlyAssignedExtendedResponseTape':
    case 'borrowingBaseReserve':
    case 'bankAccountScreenshot':
      return createFileInsertionConfigDefault({
        fileType,
        loanProductId,
        bankAccountId,
        reportingDateFormula,
        artifactSlug,
        startingExcelCoordinate,
      });
    case 'athenaQuery':
      return createFileInsertionConfigAthenaQuery({
        reportingDateFormula,
        startingExcelCoordinate,
        athenaQueryName,
      });
    default:
      return finleyAssertUnreachable(fileType);
  }
};

const CustomConfigInputs = ({
  wipConfig,
  fileType,
  sheetIdxConfigPropertyKey,
  statementStartDateFormula,
  statementEndDateFormula,
  setStatementEndDateFormula,
  setStatementStartDateFormula,
}: {
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fileType: FileInsertionType;
  sheetIdxConfigPropertyKey: string;
  statementStartDateFormula: string;
  statementEndDateFormula: string;
  setStatementStartDateFormula: React.Dispatch<React.SetStateAction<string>>;
  setStatementEndDateFormula: React.Dispatch<React.SetStateAction<string>>;
}) => {
  switch (fileType) {
    case 'statementDailySummary':
      return (
        <>
          <Flex alignItems="center" mb="4">
            <Text fontWeight="semibold" w={labelWidth}>
              Statement Start Date Formula
            </Text>

            {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) ? (
              <InputGroup w={inputWidth}>
                <InputLeftElement pointerEvents="none">
                  <Icon icon="equals" />
                </InputLeftElement>
                <Input
                  type="text"
                  value={statementStartDateFormula}
                  onChange={(evt) => {
                    setStatementStartDateFormula(evt.target.value);
                  }}
                  isInvalid={typeof statementStartDateFormula !== 'string'}
                />
              </InputGroup>
            ) : (
              <Text>{statementStartDateFormula}</Text>
            )}
          </Flex>
          <Flex alignItems="center" mb="4">
            <Text fontWeight="semibold" w={labelWidth}>
              Statement End Date Formula
            </Text>

            {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) ? (
              <InputGroup w={inputWidth}>
                <InputLeftElement pointerEvents="none">
                  <Icon icon="equals" />
                </InputLeftElement>
                <Input
                  type="text"
                  value={statementEndDateFormula}
                  onChange={(evt) => {
                    setStatementEndDateFormula(evt.target.value);
                  }}
                  isInvalid={typeof statementEndDateFormula !== 'string'}
                />
              </InputGroup>
            ) : (
              <Text>{statementEndDateFormula}</Text>
            )}
          </Flex>
        </>
      );
    case 'rawMasterLoanTape':
    case 'rawAssignedLoanTape':
    case 'rawCovenantLoanTape':
    case 'rawRepaymentTape':
    case 'rawSupplementalTape':
    case 'generatedMasterLoanTape':
    case 'generatedAssignedLoanTape':
    case 'generatedCovenantLoanTape':
    case 'generatedRepaymentTape':
    case 'generatedSupplementalTape':
    case 'intermediateTape':
    case 'extendedResponseTape':
    case 'newlyAssignedExtendedResponseTape':
    case 'borrowingBaseReserve':
    case 'bankAccountScreenshot':
    case 'athenaQuery':
      return null;
    default:
      return finleyAssertUnreachable(fileType);
  }
};

export const ReportFileInsertionConfigContainer = ({
  organizationId,
  wipConfig,
  sheetIdxConfigPropertyKey,
  targetSheetConfig,
  bankAccounts,
  fileInsertAthenaQueries,
}: ReportFileInsertionConfigContainerProps) => {
  const toast = useToast();

  // This component seemingly is nested under another "form" so had issues leveraging "formik",
  // thus setting up individual states to manage this instead.
  // This ultimately should be fine since these states will only stay local
  const [fileTypeState, setFileTypeState] = useState<FileInsertionType>(
    targetSheetConfig.fileType,
  );
  const [loanProductIdState, setLoanProductIdState] = useState<
    string | undefined
  >(targetSheetConfig.loanProductId);
  const [reportDateFormulaState, setReportDateFormulaState] = useState<string>(
    targetSheetConfig.reportingDateFormula,
  );
  const [artifactSlugState, setArtifactSlugState] = useState<
    string | undefined
  >(targetSheetConfig.artifactSlug);
  const [bankAccountIdState, setBankAccountIdState] = useState<
    string | undefined
  >(targetSheetConfig.bankAccountId);
  const [excelCoordState, setExcelCoordState] = useState<string>(
    targetSheetConfig.startingExcelCoordinate,
  );
  const [
    statementStartDateFormula,
    setStatementStartDateFormula,
  ] = useState<string>(
    targetSheetConfig.fileType === 'statementDailySummary'
      ? targetSheetConfig.statementStartDateFormula
      : '',
  );
  const [
    statementEndDateFormula,
    setStatementEndDateFormula,
  ] = useState<string>(
    targetSheetConfig.fileType === 'statementDailySummary'
      ? targetSheetConfig.statementEndDateFormula
      : '',
  );
  const [athenaQueryNameState, setAthenaQueryNameState] = useState<string>(
    targetSheetConfig.fileType === 'athenaQuery'
      ? targetSheetConfig.athenaQueryName
      : '',
  );

  const [haveValuesChanged, setHaveValuesChanged] = useState<boolean>(false);

  const { data: orgLoanProductsData } = useGetOrganizationLoanProductsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { organizationId },
  });

  const onSave = (): void => {
    if (!wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey)) {
      return;
    }

    const sanitizedFormula = reportDateFormulaState.startsWith('=')
      ? reportDateFormulaState.slice(1)
      : reportDateFormulaState;

    const updatedFileInsertionConfig: FileInsertionConfig = createFileInsertionConfig(
      {
        fileType: fileTypeState,
        loanProductId: loanProductIdState,
        bankAccountId: bankAccountIdState,
        reportingDateFormula: sanitizedFormula,
        artifactSlug: artifactSlugState?.trim() ?? undefined,
        startingExcelCoordinate: excelCoordState,
        statementStartDateFormula,
        statementEndDateFormula,
        athenaQueryName: athenaQueryNameState,
      },
    );
    const validateErrors = reportingValidate.output.validateFileInsertionConfig(
      updatedFileInsertionConfig,
      fileInsertAthenaQueries,
    );
    if (validateErrors.length > 0) {
      toast({
        title: 'Save File Insertion Config Error',
        description: validateErrors.join(', '),
        status: 'error',
        position: 'top',
        duration: 8000,
        isClosable: true,
      });
      return;
    }

    wipConfig.formProps.setFieldValue(
      sheetIdxConfigPropertyKey,
      updatedFileInsertionConfig,
    );
  };

  useEffect(() => {
    // Need to refresh states when "targetSheet" prop gets updated
    setFileTypeState(targetSheetConfig.fileType);
    setLoanProductIdState(targetSheetConfig.loanProductId);
    setReportDateFormulaState(targetSheetConfig.reportingDateFormula);
    setArtifactSlugState(targetSheetConfig.artifactSlug);
    setExcelCoordState(targetSheetConfig.startingExcelCoordinate);
    setStatementStartDateFormula(
      targetSheetConfig.fileType === 'statementDailySummary'
        ? targetSheetConfig.statementStartDateFormula
        : '',
    );
    setStatementEndDateFormula(
      targetSheetConfig.fileType === 'statementDailySummary'
        ? targetSheetConfig.statementEndDateFormula
        : '',
    );
    setAthenaQueryNameState(
      targetSheetConfig.fileType === 'athenaQuery'
        ? targetSheetConfig.athenaQueryName
        : '',
    );
  }, [targetSheetConfig]);

  useEffect(() => {
    // When changing "file type", also update any optional fields since we
    // potentially shift if they're rendered or not, so remove unseen dangling info
    if (
      !reportingValidate.output.isArtifactSlugNeededForFileType(fileTypeState)
    ) {
      setArtifactSlugState(undefined);
    }
    if (
      !reportingValidate.output.isLoanProductNeededForFileType(fileTypeState)
    ) {
      setLoanProductIdState(undefined);
    }
    if (!reportingValidate.output.isBankAccountNeedForFileType(fileTypeState)) {
      setBankAccountIdState(undefined);
    }
    if (fileTypeState !== 'athenaQuery') {
      setAthenaQueryNameState('');
    }
  }, [fileTypeState]);

  useEffect(() => {
    setHaveValuesChanged(
      targetSheetConfig.fileType !== fileTypeState ||
        targetSheetConfig.loanProductId !== loanProductIdState ||
        targetSheetConfig.reportingDateFormula !== reportDateFormulaState ||
        targetSheetConfig.artifactSlug !== artifactSlugState ||
        targetSheetConfig.bankAccountId !== bankAccountIdState ||
        targetSheetConfig.startingExcelCoordinate !== excelCoordState ||
        (targetSheetConfig.fileType === 'statementDailySummary' &&
          (targetSheetConfig.statementStartDateFormula !==
            statementStartDateFormula ||
            targetSheetConfig.statementEndDateFormula !==
              statementEndDateFormula)) ||
        (targetSheetConfig.fileType === 'athenaQuery' &&
          targetSheetConfig.athenaQueryName !== athenaQueryNameState),
    );
  }, [
    artifactSlugState,
    excelCoordState,
    fileTypeState,
    loanProductIdState,
    reportDateFormulaState,
    statementStartDateFormula,
    statementEndDateFormula,
    bankAccountIdState,
    athenaQueryNameState,
    targetSheetConfig,
  ]);

  if (!orgLoanProductsData) {
    return <Skeleton />;
  }

  const { loanProducts } = orgLoanProductsData.organization;
  const targetLoanProduct = loanProducts.find(
    ({ id }) => id === targetSheetConfig.loanProductId,
  );

  const targetBankAccount = bankAccounts.find(
    ({ entity: { id } }) => id === targetSheetConfig.bankAccountId,
  );

  return (
    <Box id="file-insertion-config-container">
      {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) && (
        <Flex alignItems="center" mb="4">
          <Button
            mr="4"
            px="2"
            leftIcon={<FiSave />}
            onClick={onSave}
            isDisabled={!haveValuesChanged}
          >
            Save Data
          </Button>
          {haveValuesChanged && (
            <Tooltip label="Unsaved changes">
              <WarningTwoIcon />
            </Tooltip>
          )}
        </Flex>
      )}

      {/* *** File Type Management *** */}
      <Flex alignItems="center" mb="4">
        <Text fontWeight="semibold" w={labelWidth}>
          File Type
        </Text>

        {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) ? (
          <Box w={inputWidth} mr="3">
            <Select<FileInsertionType>
              value={fileTypeState}
              onChange={(val) => {
                if (val) {
                  setFileTypeState(val);
                  setArtifactSlugState(undefined);
                }
              }}
              options={formatSelectOptions(fileInsertTypes, 'title')}
            />
          </Box>
        ) : (
          <Text>{commonUtils.toTitleCase(targetSheetConfig.fileType)}</Text>
        )}
      </Flex>

      {/* *** Loan Product Management *** */}
      {reportingValidate.output.isLoanProductNeededForFileType(
        fileTypeState,
      ) && (
        <Flex alignItems="center" mb="4">
          <Text fontWeight="semibold" w={labelWidth}>
            Loan Product
          </Text>

          {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) ? (
            <Box w={inputWidth} mr="3">
              <Select<string>
                value={loanProductIdState}
                isInvalid={!loanProductIdState}
                onChange={(val) => {
                  if (val) {
                    setLoanProductIdState(val);
                  }
                }}
                options={loanProducts.map(({ id, name }) => ({
                  label: name,
                  value: id,
                }))}
              />
            </Box>
          ) : (
            <Text>{targetLoanProduct?.name ?? ''}</Text>
          )}
        </Flex>
      )}

      {/* *** Reporting Date Formula Management *** */}
      <Flex alignItems="center" mb="4">
        <Text fontWeight="semibold" w={labelWidth}>
          Reporting Date Formula
        </Text>

        {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) ? (
          <InputGroup w={inputWidth}>
            <InputLeftElement pointerEvents="none">
              <Icon icon="equals" />
            </InputLeftElement>
            <Input
              type="text"
              value={reportDateFormulaState}
              onChange={(evt) => {
                setReportDateFormulaState(evt.target.value);
              }}
              isInvalid={!reportDateFormulaState}
            />
          </InputGroup>
        ) : (
          <Text>{targetSheetConfig.reportingDateFormula}</Text>
        )}
      </Flex>

      {/* *** Starting Artifact Slug Management *** */}
      {reportingValidate.output.isArtifactSlugNeededForFileType(
        fileTypeState,
      ) && (
        <Flex alignItems="center" mb="4">
          <Text fontWeight="semibold" w={labelWidth}>
            Artifact Slug
          </Text>

          {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) ? (
            <InputGroup w={inputWidth}>
              <Input
                type="text"
                value={artifactSlugState ?? ''}
                onChange={(evt) => {
                  setArtifactSlugState(evt.target.value);
                }}
              />
            </InputGroup>
          ) : (
            <Text>{targetSheetConfig.artifactSlug}</Text>
          )}
        </Flex>
      )}

      {/* *** Starting Bank Account Id Management *** */}
      {reportingValidate.output.isBankAccountNeedForFileType(fileTypeState) && (
        <Flex alignItems="center" mb="4">
          <Text fontWeight="semibold" w={labelWidth}>
            Bank Account
          </Text>

          {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) ? (
            <Box w={inputWidth} mr="3">
              <Select<string>
                value={bankAccountIdState}
                isInvalid={!bankAccountIdState}
                onChange={(val) => {
                  if (val) {
                    setBankAccountIdState(val);
                  }
                }}
                options={bankAccounts.map(({ entity: { name, id } }) => ({
                  label: name,
                  value: id,
                }))}
              />
            </Box>
          ) : (
            <Text>{targetBankAccount?.entity.name ?? ''}</Text>
          )}
        </Flex>
      )}

      {/* *** Athena Query Name Management *** */}
      {fileTypeState === 'athenaQuery' && (
        <Flex alignItems="center" mb="4">
          <Text fontWeight="semibold" w={labelWidth}>
            Athena Query Name
          </Text>

          {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) ? (
            <InputGroup w={inputWidth}>
              <Input
                type="text"
                value={athenaQueryNameState}
                onChange={(evt) => {
                  setAthenaQueryNameState(evt.target.value);
                }}
                placeholder="Enter query name from output athenaQueries"
                isInvalid={!athenaQueryNameState}
              />
            </InputGroup>
          ) : (
            <Text>
              {targetSheetConfig.fileType === 'athenaQuery'
                ? targetSheetConfig.athenaQueryName
                : ''}
            </Text>
          )}
        </Flex>
      )}

      {/* *** Starting Excel Coordinate Management *** */}
      <Flex alignItems="center" mb="4">
        <Text fontWeight="semibold" w={labelWidth}>
          Starting Excel Coordinate
        </Text>

        {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) ? (
          <InputGroup w={inputWidth}>
            <Input
              type="text"
              value={excelCoordState}
              onChange={(evt) => {
                setExcelCoordState(evt.target.value);
              }}
              isInvalid={!excelCoordState}
            />
          </InputGroup>
        ) : (
          <Text>{targetSheetConfig.startingExcelCoordinate}</Text>
        )}
      </Flex>
      <CustomConfigInputs
        wipConfig={wipConfig}
        fileType={fileTypeState}
        sheetIdxConfigPropertyKey={sheetIdxConfigPropertyKey}
        statementStartDateFormula={statementStartDateFormula}
        setStatementStartDateFormula={setStatementStartDateFormula}
        statementEndDateFormula={statementEndDateFormula}
        setStatementEndDateFormula={setStatementEndDateFormula}
      />
    </Box>
  );
};
